import { ReactElement, useEffect, useState } from 'react';

import { useNavigate } from 'react-router-dom';

import Alert from '@mui/material/Alert';
import Button from '@mui/material/Button';
import MenuItem from '@mui/material/MenuItem';
import Select from '@mui/material/Select';
import Snackbar from '@mui/material/Snackbar';
import Stack from '@mui/material/Stack';
import TextField from '@mui/material/TextField';
import Typography from '@mui/material/Typography';

import GratiPageWrapper from '../components/common/GratiPageWrapper';
import PersonAutocomplete from '../components/person/PersonAutocomplete';
import { useApp } from '../contextProviders/AppProvider';
import useAllMembers from '../dataHooks/useAllMembers';
import { useGuidanceActionMutators } from '../dataMutators/useGuidanceActionMutators';
import { ALL_GUIDANCE_ACTION_TYPES, GUIDANCE_ACTION_TYPES, GuidanceActionType } from '../types/GuidanceAction';
import PersonIdentity from '../types/PersonIdentity';

/** Context field definitions per action type. */
const CONTEXT_FIELDS: Record<GuidanceActionType, { key: string; label: string; required: boolean }[]> = {
  [GUIDANCE_ACTION_TYPES.TEAM_MANAGER_SETUP_ROSTER]: [
    { key: 'orgId',    label: 'Org ID',    required: true },
    { key: 'teamId',   label: 'Team ID',   required: true },
    { key: 'teamName', label: 'Team name', required: false },
  ],
};

export default function GuidanceActionCreatorPage(): ReactElement {
  const { setPageName } = useApp();
  const navigate = useNavigate();
  const { createGuidanceActionForProfile } = useGuidanceActionMutators();

  const [selectedPersonIdentity, setSelectedPersonIdentity] = useState<PersonIdentity | null>(null);
  const [actionType, setActionType] = useState<GuidanceActionType>(ALL_GUIDANCE_ACTION_TYPES[0]);
  const [contextValues, setContextValues] = useState<Record<string, string>>({});
  const [customTitle, setCustomTitle] = useState('');
  const [customDescription, setCustomDescription] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [showSuccess, setShowSuccess] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    setPageName('Send Guidance Action');
  }, [setPageName]);

  // Reset context fields when action type changes
  useEffect(() => {
    setContextValues({});
  }, [actionType]);

  const usePeopleHook = () => {
    const { persons, isLoading, error } = useAllMembers();
    return {
      items: persons ?? [],
      isLoading,
      error: error ?? undefined,
    };
  };

  const contextFields = CONTEXT_FIELDS[actionType] ?? [];

  const isValid =
    !!selectedPersonIdentity &&
    contextFields.filter((f) => f.required).every((f) => !!contextValues[f.key]?.trim());

  const handleSubmit = async () => {
    if (!selectedPersonIdentity || isSubmitting) return;
    setIsSubmitting(true);
    setError(null);
    try {
      const context = Object.fromEntries(
        Object.entries(contextValues).filter(([, v]) => !!v.trim())
      );
      await createGuidanceActionForProfile(
        selectedPersonIdentity.itemId,
        actionType,
        Object.keys(context).length > 0 ? context : undefined,
        {
          ...(customTitle.trim() && { title: customTitle.trim() }),
          ...(customDescription.trim() && { description: customDescription.trim() }),
        },
      );
      setShowSuccess(true);
      setSelectedPersonIdentity(null);
      setContextValues({});
      setCustomTitle('');
      setCustomDescription('');
    } catch (err) {
      console.error('Error creating guidance action:', err);
      setError('Failed to create guidance action. Please try again.');
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <GratiPageWrapper>
      <Stack spacing={2} sx={{ maxWidth: '30em' }}>
        <PersonAutocomplete<PersonIdentity>
          usePeopleHook={usePeopleHook}
          label="Recipient"
          placeholder="Search by name"
          value={selectedPersonIdentity}
          onChange={(_, person) => setSelectedPersonIdentity(person)}
          size="small"
          clearable
        />

        <Stack spacing={0.5}>
          <Typography variant="body2" color="text.secondary">Action type</Typography>
          <Select
            size="small"
            value={actionType}
            onChange={(e) => setActionType(e.target.value as GuidanceActionType)}
          >
            {ALL_GUIDANCE_ACTION_TYPES.map((type) => (
              <MenuItem key={type} value={type}>{type}</MenuItem>
            ))}
          </Select>
        </Stack>

        {contextFields.length > 0 && (
          <Stack spacing={1}>
            {contextFields.map(({ key, label, required }) => (
              <TextField
                key={key}
                size="small"
                label={`${label}${required ? '' : ' (optional)'}`}
                value={contextValues[key] ?? ''}
                onChange={(e) => setContextValues((prev) => ({ ...prev, [key]: e.target.value }))}
              />
            ))}
          </Stack>
        )}

        <Stack spacing={1}>
          <TextField
            size="small"
            label="Custom title (optional — overrides default)"
            value={customTitle}
            onChange={(e) => setCustomTitle(e.target.value)}
          />
          <TextField
            size="small"
            label="Custom description (optional — overrides default)"
            value={customDescription}
            onChange={(e) => setCustomDescription(e.target.value)}
            multiline
            rows={2}
          />
        </Stack>

        {error && <Alert severity="error">{error}</Alert>}

        <Stack direction="row" spacing={1}>
          <Button
            variant="contained"
            disabled={!isValid || isSubmitting}
            onClick={handleSubmit}
          >
            Send
          </Button>
          <Button variant="outlined" onClick={() => navigate(-1)}>
            Cancel
          </Button>
        </Stack>
      </Stack>

      <Snackbar
        open={showSuccess}
        autoHideDuration={3000}
        onClose={() => setShowSuccess(false)}
        message="Guidance action sent"
      />
    </GratiPageWrapper>
  );
}
